import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  applyOptimisticAddReaction,
  applyOptimisticRemoveReaction,
  getPartnerCommunityDisplayName,
} from '../components/Partner/Community/communityChatReactions';
import {
  buildPartnerGroupChannelMessageMapOptions,
  hasMeaningfulReactionFields,
  mapPartnerGroupChannelApiReactionResponse,
} from '../components/Partner/Community/mapPartnerGroupChannel';
import {
  applyPartnerGroupChatOptimisticReactionInCache,
  updatePartnerGroupChatMessageReactionsInCache,
} from '../components/Partner/Community/partnerGroupChannelMessagesCache';
import {
  removePartnerGroupChannelMessageReaction,
  setPartnerGroupChannelMessageReaction,
} from '../services/partnerGroupChannel';
import { partnerGroupChannelQueryKeys } from './partner-group-channel-query-keys';

export const PARTNER_GROUP_CHAT_REACTION_ACTION = {
  ADD: 'add',
  REMOVE: 'remove',
};

export const usePartnerGroupChatReactionMutation = (options = {}) => {
  const { onSuccess, onError, ...restOptions } = options;
  const queryClient = useQueryClient();
  const { firebaseUid, partnerProfile } = useAuth();
  const messageMapOptions = useMemo(
    () => buildPartnerGroupChannelMessageMapOptions({ firebaseUid, partnerProfile }),
    [firebaseUid, partnerProfile],
  );

  const reactionActor = useMemo(() => {
    const nested =
      partnerProfile?.partnerProfile && typeof partnerProfile.partnerProfile === 'object'
        ? partnerProfile.partnerProfile
        : null;

    return {
      id:
        partnerProfile?.id ??
        partnerProfile?.partner_profile_id ??
        partnerProfile?.partnerProfileId ??
        nested?.id ??
        null,
      uid: firebaseUid ?? partnerProfile?.uid ?? nested?.uid ?? null,
      fullName:
        getPartnerCommunityDisplayName(partnerProfile) ||
        partnerProfile?.fullName ||
        nested?.fullName ||
        'You',
      avatarUrl:
        partnerProfile?.avatarUrl ??
        nested?.avatarUrl ??
        partnerProfile?.logoUrl ??
        nested?.logoUrl ??
        null,
    };
  }, [firebaseUid, partnerProfile]);

  return useMutation({
    mutationFn: async ({
      messageId,
      type,
      action = PARTNER_GROUP_CHAT_REACTION_ACTION.ADD,
      channelId,
    }) => {
      const isRemove = action === PARTNER_GROUP_CHAT_REACTION_ACTION.REMOVE;
      const result = isRemove
        ? await removePartnerGroupChannelMessageReaction(messageId, { type, channelId })
        : await setPartnerGroupChannelMessageReaction(messageId, {
            type,
            enforce_unique: false,
            channelId,
          });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update reaction');
      }

      return {
        messageId,
        type,
        action,
        reactionFields: mapPartnerGroupChannelApiReactionResponse(result.data, messageMapOptions),
      };
    },
    onMutate: async ({ messageId, type, action = PARTNER_GROUP_CHAT_REACTION_ACTION.ADD }) => {
      await queryClient.cancelQueries({
        queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
      });

      const applyOptimistic =
        action === PARTNER_GROUP_CHAT_REACTION_ACTION.REMOVE
          ? (message, reactionType) =>
              applyOptimisticRemoveReaction(message, reactionType, { user: reactionActor })
          : (message, reactionType) =>
              applyOptimisticAddReaction(message, reactionType, { user: reactionActor });

      const applied = applyPartnerGroupChatOptimisticReactionInCache(
        queryClient,
        messageId,
        type,
        applyOptimistic,
      );

      return { messageId, type, action, applied };
    },
    onSuccess: (result, variables, context) => {
      // Only replace optimistic chips when the API returned real reaction state.
      // Status-only bodies used to map to empty fields and wipe the UI until reload.
      if (result?.messageId && hasMeaningfulReactionFields(result.reactionFields)) {
        const updated = updatePartnerGroupChatMessageReactionsInCache(
          queryClient,
          result.messageId,
          result.reactionFields,
        );
        if (!updated) {
          queryClient.invalidateQueries({
            queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
          });
        }
      } else if (!context?.applied) {
        queryClient.invalidateQueries({
          queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
        });
      }
      onSuccess?.(result, variables, context);
    },
    onError: (error, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
      });
      toast.error(error?.message || 'Failed to update reaction');
      onError?.(error, variables, context);
    },
    ...restOptions,
  });
};

export default usePartnerGroupChatReactionMutation;
